// Le MÉMO (R-MEM-02, R-MEM-03) : toutes les notions enseignées jusqu'ici, cherchables.
// Il existe parce qu'une notion oubliée envoyait chercher ailleurs, et qu'une séance interrompue
// par vingt minutes de recherche n'est plus une séance de 60 minutes.
import { useState } from 'react';
import { Link } from 'react-router';
import { useMagasin } from '../donnees/magasin';
import { memo, chercherDansLeMemo } from '@partage/programme';
import type { NotionCompilee } from '@partage/contenu';
import { Html } from '../composants/Html';

function Fiche({ n }: { n: NotionCompilee }) {
  return (
    <div className="carte">
      <div className="ligne">
        <h3 style={{ margin: 0 }}><code>{n.nom}</code></h3>
        <span className="espace" />
        <Link className="badge" to={`/sessions/${n.session}`} title={n.titre_session}>{n.session}</Link>
      </div>
      <p className="doux" style={{ marginTop: '0.3em' }}><Html html={n.quand_html} enLigne /></p>
      <Html html={n.syntaxe_html} />
      <details>
        <summary>Un exemple</summary>
        <Html html={n.exemple_html} />
      </details>
      {n.piege_html ? <p><strong>Le piège : </strong><Html html={n.piege_html} enLigne /></p> : null}
    </div>
  );
}

export function Memo() {
  const { etat, bundles, parcours } = useMagasin();
  const [recherche, setRecherche] = useState('');
  const [tout, setTout] = useState(false);

  // R-MEM-03 : par défaut, seules les notions des séances terminées — le mémo ne doit rien
  // révéler d'une séance qu'on n'a pas encore faite.
  const notions = memo(etat, Object.values(bundles), Object.values(parcours), !tout);
  const trouvees = chercherDansLeMemo(notions, recherche);

  return (
    <>
      <h2>Mémo</h2>
      <p className="doux">
        Tout ce que les leçons t’ont appris, la dernière notion en haut. C’est ici que tu cherches
        quand une syntaxe t’échappe en pleine séance — pas ailleurs.
      </p>

      <div className="carte">
        <div className="ligne">
          <input
            type="search"
            placeholder="Chercher : get, f-string, argv…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            style={{ flex: 1 }}
            aria-label="Chercher dans le mémo"
          />
          <span className="badge">{trouvees.length}</span>
        </div>
        <label className="petit-texte" style={{ display: 'block', marginTop: '0.5em' }}>
          <input type="checkbox" checked={tout} onChange={(e) => setTout(e.target.checked)} />{' '}
          Inclure les séances que je n’ai pas encore faites
        </label>
      </div>

      {trouvees.length === 0 ? (
        <p className="vide">
          {notions.length === 0
            ? 'Le mémo se remplit à mesure que tu termines des séances.'
            : `Rien pour « ${recherche} » dans les ${notions.length} notions vues.`}
        </p>
      ) : (
        trouvees.map((n) => <Fiche n={n} key={n.id} />)
      )}
    </>
  );
}
